/**
 * sidebar-nav.ts — Pure nav-item visibility for the app Sidebar.
 *
 * Extracted so characterization tests can pin per-role visibility without
 * mounting the full Sidebar (stores, React Query, Zendesk, etc.). Product
 * gating behaviour is unchanged — Sidebar filters through this helper.
 */

import type { ReleaseFlag } from '@/lib/release-flags';
import { canAccessRoute, isSSIRole } from '@/lib/permissions';

export type SidebarNavChild = {
  label: string;
  path: string;
  /** When set, hide unless the release flag is enabled. */
  flag?: ReleaseFlag;
};

export type SidebarNavItem = {
  label: string;
  path: string;
  adminOnly?: boolean;
  /** When true, hide unless the user's role passes canAccessRoute(path). */
  permissionGated?: boolean;
  /** When set, hide unless the release flag is enabled. */
  flag?: ReleaseFlag;
  children?: SidebarNavChild[];
};

/**
 * Top-level sidebar entries (labels + paths + gates). Icons live in Sidebar.tsx
 * so this module stays free of lucide imports for unit tests.
 */
export const SIDEBAR_NAV_ITEMS: ReadonlyArray<SidebarNavItem> = [
  { label: 'Portfolio Overview', path: '/dashboard', permissionGated: true },
  { label: 'Opportunities', path: '/opportunities', flag: 'opportunities' },
  { label: 'Properties', path: '/properties' },
  { label: 'Reports', path: '/reports', permissionGated: true },
  { label: 'Teams', path: '/teams', permissionGated: true },
  {
    label: 'Analytics',
    path: '/reports?tab=value-opportunity',
    permissionGated: true,
    flag: 'analytics-nav',
  },
  // US-DVU-06: gate Orders + Drawing Vault so DrawingVaultUser fail-closed works
  // and the former default-open door is closed. Existing eight roles remain on
  // the ROUTE_PERMISSIONS allowlists.
  { label: 'Orders', path: '/orders', permissionGated: true },
  { label: 'Drawing Vault', path: '/drawing-vault', permissionGated: true },
  {
    label: 'Vault Reports',
    path: '/drawings/reports',
    flag: 'vault-reports',
    permissionGated: true,
  },
  { label: 'Alerts', path: '/alerts', permissionGated: true },
  { label: 'Messages', path: '/messages', flag: 'messages' },
  { label: 'Support', path: '/support' },
  {
    label: 'Admin',
    path: '/admin',
    adminOnly: true,
    children: [
      { label: 'Companies', path: '/admin/companies' },
      // SSI-only via adminOnly parent — not behind admin-v3-extras (see release-flags.ts).
      { label: 'Drawing Vault Admin', path: '/admin/drawing-vault' },
      // SSI-internal roadmap. My Work is this person's lanes and sprint queue.
      // Activity lives as a button on those pages, not as an extra Admin child.
      { label: 'My Work', path: '/admin/roadmap/my-work' },
      { label: 'Roadmap', path: '/admin/roadmap' },
      { label: 'Sprints', path: '/admin/roadmap/sprints' },
      { label: 'Tickets', path: '/admin/roadmap/requests' },
      {
        label: 'Property Imports',
        path: '/admin/property-imports',
        flag: 'admin-v3-extras',
      },
      {
        label: 'Subscription Limits',
        path: '/admin/subscription-limits',
        flag: 'admin-v3-extras',
      },
      {
        label: 'Widget Permissions',
        path: '/admin/widget-permissions',
        flag: 'admin-v3-extras',
      },
      { label: 'Settings', path: '/admin/settings' },
      {
        label: 'Report Reviews',
        path: '/reports/reviews',
        flag: 'admin-v3-extras',
      },
      { label: 'Pricing', path: '/admin/pricing', flag: 'admin-v3-extras' },
      { label: 'Domains', path: '/admin/domains', flag: 'admin-v3-extras' },
      {
        label: 'Subscriptions',
        path: '/admin/subscriptions',
        flag: 'admin-v3-extras',
      },
      {
        label: 'Email Templates',
        path: '/admin/email-templates',
        flag: 'admin-v3-extras',
      },
    ],
  },
];

/**
 * Paths hidden when the dashboard/tier gate is off (BASE TruSpace clients).
 *
 * Drawing Vault is grant-driven (ADR-0019) — Sidebar hides it via
 * `useDrawingVaultNavHidden`, not this set. Vault Reports is SSI-only via
 * {@link canAccessRoute}; BASE hide here is a belt-and-suspenders for the
 * dashboard path only once non-SSI roles are already denied the reports route.
 */
const BASE_TIER_HIDDEN_PATHS = new Set(['/dashboard', '/drawings/reports']);

export type SidebarVisibilityContext = {
  /** Release-flag resolver (injectable for tests). */
  isReleaseFeatureEnabled: (flag: ReleaseFlag) => boolean;
  /** BASE-tier clients hide Portfolio Overview (+ Vault Reports if a role could see it). */
  hideDashboardNav: boolean;
  /**
   * DEF-427 — Report Review window uses the Company Manager nav surface even when
   * the signed-in user is SSI (legacy `sertReportReviewAsCompanyManagerPerspective`).
   * Hides Admin / Vault Admin / Portfolio Overview and any SSI-only entries.
   */
  reportReviewCmPerspective?: boolean;
};

/** Paths SSI must not see while reviewing a report as a Company Manager (DEF-427). */
const REPORT_REVIEW_CM_HIDDEN_PATHS = new Set([
  '/dashboard',
  '/admin',
  '/drawings/reports',
]);

/**
 * Whether a top-level sidebar item is visible for `userRole` under `ctx`.
 *
 * @param item - Nav item from SIDEBAR_NAV_ITEMS.
 * @param userRole - Authenticated user's role string.
 * @param ctx - Flag + dashboard-tier context.
 * @returns `true` when the item should render.
 */
export function isSidebarNavItemVisible(
  item: SidebarNavItem,
  userRole: string,
  ctx: SidebarVisibilityContext,
): boolean {
  if (item.flag && !ctx.isReleaseFeatureEnabled(item.flag)) return false;
  // DEF-427: evaluate CM perspective before adminOnly so SSI review windows
  // drop Admin / Vault Admin / Dashboard even though the JWT role is SSI.
  const effectiveRole = ctx.reportReviewCmPerspective ? 'CompanyManager' : userRole;
  if (ctx.reportReviewCmPerspective && REPORT_REVIEW_CM_HIDDEN_PATHS.has(item.path)) {
    return false;
  }
  if (item.adminOnly && !isSSIRole(effectiveRole)) return false;
  // US-DVU-06: vault-only role is fail-closed — hide any nav item whose route is denied
  // (covers ungated items like Properties that other roles still see by default).
  if (effectiveRole === 'DrawingVaultUser' && !canAccessRoute(effectiveRole, item.path)) {
    return false;
  }
  if (item.permissionGated && !canAccessRoute(effectiveRole, item.path)) return false;
  if (ctx.hideDashboardNav && BASE_TIER_HIDDEN_PATHS.has(item.path)) return false;
  return true;
}

/**
 * Visible top-level sidebar labels for a role (characterization helper).
 *
 * @param userRole - Authenticated user's role string.
 * @param ctx - Flag + dashboard-tier context.
 * @returns Ordered labels that pass the current visibility filter.
 */
export function visibleSidebarLabels(
  userRole: string,
  ctx: SidebarVisibilityContext,
): string[] {
  return SIDEBAR_NAV_ITEMS.filter((item) =>
    isSidebarNavItemVisible(item, userRole, ctx),
  ).map((item) => item.label);
}

/**
 * Which Admin child is selected. Roadmap is exact — My Work, Sprints, and
 * Tickets live under its path. Activity is not a sidebar item; it sits under
 * Tickets so the left nav still shows where you are. Sprint drill-ins sit
 * under Sprints.
 */
export function isAdminChildNavActive(childPath: string, pathname: string): boolean {
  if (childPath === '/admin/roadmap') {
    return pathname === '/admin/roadmap';
  }
  if (childPath === '/admin/roadmap/my-work') {
    return pathname === childPath || pathname.startsWith(`${childPath}/`);
  }
  if (childPath === '/admin/roadmap/sprints') {
    return pathname === childPath || pathname.startsWith(`${childPath}/`);
  }
  if (childPath === '/admin/roadmap/requests') {
    return (
      pathname.startsWith('/admin/roadmap/requests') ||
      pathname.startsWith('/admin/roadmap/activity') ||
      pathname.startsWith('/admin/roadmap/mine')
    );
  }
  return pathname === childPath || pathname.startsWith(`${childPath}/`);
}
